import type {
    CodecConfigurer,
    DefaultCodecs,
    HttpMessageReader,
    HttpMessageWriter,
    ServerCodecConfigurer,
    ServerDefaultCodecs
} from './types.ts';
import type { HttpOutputMessage, MutableHttpHeaders } from '../../../types/web/http';
import { AbstractEncoder, BlobEncoder, StringEncoder } from '../../codec/encoder.ts';
import type { Encoder } from '../../codec/types.ts';
import { MediaType } from '../media-type.ts';
import { isConcrete } from '../../utils/mime-type-utils.ts';
import { getContentType } from '../headers.ts';
import { DefaultPartHttpMessageReader, MultipartHttpMessageReader } from './multipart.ts';


class EncoderHttpMessageWriter<T> implements HttpMessageWriter {
    readonly #encoder: Encoder<T>;
    readonly #mediaTypes: readonly MediaType[];
    #defaultMediaType?: MediaType;

    constructor(encoder: Encoder<T>) {
        EncoderHttpMessageWriter.initLogger(encoder);
        this.#encoder = encoder;
        this.#mediaTypes = MediaType.asMediaTypes(...encoder.encodableMimeTypes());
        this.#defaultMediaType = this.#mediaTypes.find(mediaType => isConcrete(mediaType));
    }

    get encoder(): Encoder<T> {
        return this.#encoder;
    }

    getWriteableMediaTypes(): ReadonlyArray<MediaType> {
        return this.#mediaTypes;
    }

    canWrite(type, mediaType?: MediaType): boolean {
        return this.#encoder.canEncode(type, mediaType);
    }

    async write(input: Promise<T> | T, type, message: HttpOutputMessage<MutableHttpHeaders>, mediaType?: MediaType, hints?: Map<string, unknown>): Promise<void> {
        const resolvedMediaType = this.#updateContentType(message, mediaType);
        const part = this.#encoder.encode(input, resolvedMediaType, hints);
        await message.body(part);
    }

    private static initLogger(encoder: Encoder<unknown>): void {
        if (encoder instanceof AbstractEncoder) {
            // encoder.setLogger();
        }
    }

    #updateContentType(message: HttpOutputMessage<MutableHttpHeaders>, mediaType?: MediaType) {
        let resolvedMediaType = getContentType(message.headers);
        if (resolvedMediaType) {
            return resolvedMediaType;
        }
        let fallback = this.#defaultMediaType;
        resolvedMediaType = (useFallback(mediaType, fallback) ? fallback : mediaType);
        if (resolvedMediaType) {
            resolvedMediaType = addDefaultCharsetIfNeeded(resolvedMediaType, fallback);
            message.headers.set('Content-Type', resolvedMediaType.toString());
        }
    }
}

function addDefaultCharsetIfNeeded(main: MediaType, defaultType?: MediaType): MediaType {
    if (!main.params.has('charset') && defaultType !== undefined) {
        const defaultCharset = defaultType.params.get('charset');
        if (defaultCharset) {
            const result = MediaType.parseMediaType(main.toString());
            result.params.set('charset', defaultCharset);
            return result;
        }
    }
    return main;
}

function useFallback(main?: MediaType, fallback?: MediaType): boolean {
    return (main === undefined || !isConcrete(main) || main.essence === MediaType.APPLICATION_OCTET_STREAM.essence) && fallback !== undefined;
}

class BaseDefaultCodecs implements DefaultCodecs {
    #multipartReader?: HttpMessageReader<unknown>;
    #maxInMemorySize?: number;
    #enableLoggingRequestDetails?: boolean;
    #registerDefaults = true;
    #writers = Array<HttpMessageWriter>();
    #readers = Array<HttpMessageReader>();

    constructor() {
        this.initReaders();
        this.initWriters();
    }

    multipartReader(reader: HttpMessageReader<unknown>) {
        this.#multipartReader = reader;
        this.initReaders();
    }

    registerDefaults(registerDefaults: boolean) {
        if (this.#registerDefaults !== registerDefaults) {
            this.#registerDefaults = registerDefaults;
            this.initReaders();
            this.initWriters();
        }
    }

    protected initReaders(): void {
        this.#readers.length = 0;
        if (!this.#registerDefaults) {
            return;
        }

        if (this.#multipartReader !== undefined) {
            this.addCodec(this.#readers, this.#multipartReader);
        }
        else {
            const partReader = new DefaultPartHttpMessageReader();
            this.addCodec(this.#readers, partReader);
            this.addCodec(this.#readers, new MultipartHttpMessageReader(partReader));
        }
    }

    protected initWriters(): void {
        this.#writers.length = 0;
        if (!this.#registerDefaults) {
            return;
        }
        this.#writers.push(...this.#getBaseWriters());
        this.extendWriters(this.#writers);
    }

    protected extendWriters(writers: Array<HttpMessageWriter>): void {
    }

    #getBaseWriters(): HttpMessageWriter[] {
        if (!this.#registerDefaults) {
            return [];
        }
        const writers = Array<HttpMessageWriter>();
        this.addCodec(writers, new EncoderHttpMessageWriter(new BlobEncoder()));
        return writers;
    }

    maxInMemorySize(bytes: number) {
        if (this.#maxInMemorySize !== bytes) {
            this.#maxInMemorySize = bytes;
            this.initReaders();
        }
    }

    enableLoggingRequestDetails(enable: boolean) {
        if (this.#enableLoggingRequestDetails !== enable) {
            this.#enableLoggingRequestDetails = enable;
            this.initReaders();
            this.initWriters();
        }
    }

    get catchAllReaders() {
        if (!this.#registerDefaults) {
            return [];
        }
        const readers = Array<HttpMessageReader>();

        return readers;
    }

    get typedWriters() {
        return this.#writers;
    }

    get typedReaders() {
        return this.#readers;
    }

    get catchAllWriters(): readonly HttpMessageWriter[]{
        if (!this.#registerDefaults) {
            return [];
        }
        const result = Array<HttpMessageWriter>();
        this.addCodec(result, new EncoderHttpMessageWriter(StringEncoder.allMimeTypes()));
        return result;
    }

    protected addCodec<T >(codecs: Array<T>, codec: T) {
        this.#initCodec(codec);
        codecs.push(codec);
    }

    #initCodec(codec: unknown): void {
        if (codec instanceof EncoderHttpMessageWriter) {
            codec = codec.encoder;
        }
        if (codec === undefined) {
            return;
        }
        const size = this.#maxInMemorySize;
        if (size !== undefined) {
            // todo
        }

        const enable = this.#enableLoggingRequestDetails;
        if (enable !== undefined) {
            if (codec instanceof MultipartHttpMessageReader) {
                codec.enableLoggingRequestDetails = enable;
            }
            if (codec instanceof DefaultPartHttpMessageReader) {
                codec.enableLoggingRequestDetails = enable;
            }
        }
    }
}
abstract class BaseCodecConfigurer implements CodecConfigurer {
    protected readonly baseDefaultCodecs: BaseDefaultCodecs;

    protected constructor(defaultCodecs: BaseDefaultCodecs) {
        this.baseDefaultCodecs = defaultCodecs;
    }

    get defaultCodecs(): DefaultCodecs {
        return this.baseDefaultCodecs;
    }

    registerDefaults(registerDefaults: boolean) {
        this.baseDefaultCodecs.registerDefaults(registerDefaults);
    }

    get readers() {
        const result = new Array<HttpMessageReader>();
        result.push(...this.baseDefaultCodecs.typedReaders);
        result.push(...this.baseDefaultCodecs.catchAllReaders);
        return result;
    }

    get writers(): readonly HttpMessageWriter[] {
        const result = new Array<HttpMessageWriter>();
        result.push(...this.baseDefaultCodecs.typedWriters);
        result.push(...this.baseDefaultCodecs.catchAllWriters);
        return result;
    }

}

class ServerDefaultCodecsImpl extends BaseDefaultCodecs implements ServerDefaultCodecs {

}
class DefaultServerCodecConfigurer extends BaseCodecConfigurer implements ServerCodecConfigurer {
    constructor() {
        super(new ServerDefaultCodecsImpl());
    }
}

function createServerCodecConfigurer(): ServerCodecConfigurer {
    return new DefaultServerCodecConfigurer();
}

export default {
    serverConfigurer: {
        create: createServerCodecConfigurer
    }
}
